import {
  Box,
  Button,
  FormControl,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  InputGroup,
  Input,
  InputRightElement,
  SimpleGrid,
  useBoolean,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Text,
  Icon,
  Divider,
  useDisclosure,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { Formik, Form, ErrorMessage } from 'formik';
import React, { useRef, useEffect, useState } from 'react';
import { ColorResult } from 'react-color';
import { useTranslation } from 'react-i18next';

import { IconArrowLeft } from 'assets/icons';
import DayPicker from 'components/DayPicker';
import { DrawerFixedButton } from 'components/Drawer';
import {
  FormikCheckbox,
  FormikDatepicker,
  FormikInput,
  FormikSelect,
  FormikSelectAsyncPaginate,
  FormikTextArea,
} from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import { ConfirmationModal } from 'components/Modal';
import ReactColor from 'components/ReactColor';
import EmployeesAndGroups from 'containers/Scheduling/RightDrawer/FormikEmployeesAndGroups';
import { MONTH_DAY_YEAR_FORMAT } from 'global/appConstants';
import { repeatTypeOptions } from 'global/options';
import { roles } from 'global/rolesAndPermissions';
import { useFetchTimezones } from 'hooks/companySettings/useFetchTimezone';
import { fetchPaginatedJobs } from 'hooks/jobs/useFetchPaginatedJobs';
import { useFetchScheduleColors } from 'hooks/scheduling/useFetchScheduleColors';
import { ISaveScheduleData, useSaveSchedule } from 'hooks/scheduling/useSaveSchedule';
import { useCompanyInfo, useUserObject } from 'store/useUserStore';
import { getAsyncSelectLoadOptions, isEmptyObjectWithNull } from 'utils/helpers';

import { initialScheduleFormData, scheduleFormValidationSchema } from './schema';

interface IScheduleFormProps {
  onClose?: () => void;
  formData?: typeof initialScheduleFormData;
  showLoader?: boolean;
}

const ScheduleForm: React.FC<IScheduleFormProps> = ({
  onClose,
  formData = initialScheduleFormData,
  showLoader = false,
}) => {
  const { t } = useTranslation(['scheduling', 'common']);
  const userStore = useUserObject();

  const companyInfo = useCompanyInfo();

  const formikRef = useRef<any>(null);

  const [allShift, setAllShift] = useState<boolean>(false);
  const [flag, setFlag] = useBoolean();
  const {
    isOpen: isConfirmEditEntireShiftModalOpen,
    onOpen: onConfirmEditEntireShiftModalOpen,
    onClose: onConfirmEditEntireShiftModalClose,
  } = useDisclosure();

  const { data: timezoneOptions, status: timezoneStatus } = useFetchTimezones();
  const { data: colorsData } = useFetchScheduleColors();
  const { mutateAsync: mutateScheduleAsync, status } = useSaveSchedule();

  useEffect(() => {
    if (userStore?.userInfo?.roleId === roles.EMPLOYEE && formikRef.current) {
      formikRef.current.setFieldValue('user_ids', [userStore?.userInfo]);
    }

    if (companyInfo?.timezone && !formData?.timezone && formikRef.current) {
      formikRef.current.setFieldValue('timezone', companyInfo?.timezone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const jobLoadOptions = async (search: any, page: any, userIds: any[]) => {
    const userList = userIds.length ? userIds.map((item: any) => item.userId).join(',') : '';

    return getAsyncSelectLoadOptions(fetchPaginatedJobs, { search, page, job_status: 1, userId: userList });
  };

  const handleDayChange = (
    dayValues: Array<any>,
    day: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    const dayIndex = dayValues.findIndex((selectedDay: any) => selectedDay.value === day.value);

    if (dayIndex !== -1) {
      const updatedValue = dayValues.filter((item: any) => item.value !== day.value);
      setFieldValue('weeks', updatedValue);
    } else {
      const updatedValue = [...dayValues, day];
      setFieldValue('weeks', updatedValue);
    }
  };

  const handleAllDayChangeCallback = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (e.target.checked) {
      setFieldValue('start_time', '00:00:00');
      setFieldValue('end_time', '23:59:00');
    }
  };

  const handleShowAddress = (
    option: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    const addressFields = ['street', 'city', 'state', 'country', 'zipCode'];

    if (!isEmptyObjectWithNull(option?.address)) {
      addressFields.forEach((address: string) => {
        setFieldValue(address, option.address[address] ?? '');
      });
    } else {
      addressFields.forEach((address: string) => setFieldValue(address, ''));
    }
  };

  const handleFormSubmit = (values: typeof initialScheduleFormData) => {
    let repeatData = {
      repeat_type: '',
      repeat_end_date: '',
      weeks: '',
      all_shifts: allShift,
    };

    if (values.repeat.includes('yes')) {
      repeatData = {
        repeat_type: values.repeat_type?.value || '',
        repeat_end_date: values.repeat_end_date ? format(new Date(values.repeat_end_date), MONTH_DAY_YEAR_FORMAT) : '',
        all_shifts: allShift,
        weeks:
          values.repeat_type && values.repeat_type?.value !== 'daily' && values.repeat_type?.value !== 'monthly'
            ? values.weeks.length
              ? values.weeks.map((item: any) => item.value).join(',')
              : ''
            : '',
      };
    }

    let addressData = {
      street: values.street || '',
      city: values.city || '',
      state: values.state || '',
      country: values.country || '',
      zip_code: values.zipCode || '',
    };

    if (values.job_id && !values.show_address?.includes('yes')) {
      addressData = {
        ...addressData,
        street: values.job_id?.address.street || '',
        city: values.job_id?.address.city || '',
        state: values.job_id?.address.state || '',
        country: values.job_id?.address.country || '',
        zip_code: values.job_id?.address.zipCode || '',
      };
    }

    if (!values.job_id && !values.show_address?.includes('yes')) {
      addressData = {
        street: '',
        city: '',
        state: '',
        country: '',
        zip_code: '',
      };
    }

    const postData: ISaveScheduleData = {
      id: values.id,
      event_pid: values.event_pid,
      title: values.title,
      description: values.description,
      job_id: values.job_id?.id || null,
      timezone:
        typeof values.timezone === 'object'
          ? companyInfo.timezone === values.timezone?.value
            ? null
            : values.timezone.value
          : companyInfo.timezone === values.timezone
            ? null
            : values.timezone,
      repeat: values.repeat.includes('yes') ? true : false,
      all_day: values.all_day.includes('yes') ? true : false,
      color: values.color
        ? colorsData?.payload.find((item: any) => item.hex_code.toLowerCase() === values.color.toLowerCase())?.color
        : '',
      user_ids: values.user_ids.map((item: any) => item.userId),
      publish: values.publish,
      update_series: values.update_series,
      start_date: values.start_date ? format(new Date(values.start_date), MONTH_DAY_YEAR_FORMAT) : '',
      end_date: values.end_date ? format(new Date(values.end_date), MONTH_DAY_YEAR_FORMAT) : '',
      start_time: values.start_time,
      end_time: values.end_time,
      use_different_address: values.show_address?.includes('yes') ? true : false,
      ...addressData,
      ...repeatData,
    };

    mutateScheduleAsync(postData).then(() => {
      if (onClose) onClose();
    });
  };

  return (
    <>
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          {flag ? (
            <>
              <IconButton
                aria-label="back"
                icon={<Icon as={IconArrowLeft} />}
                variant="ghost"
                p="1"
                cursor="pointer"
                size="sm"
                mr="2"
                sx={{ '& path': { fill: 'black' } }}
                onClick={setFlag.off}
              />
              {t('common:selectUsers')}
            </>
          ) : formData.id ? (
            t('scheduling:updateSchedule')
          ) : (
            t('scheduling:addSchedule')
          )}
        </DrawerHeader>
        <DrawerBody pb="100px">
          <Formik
            enableReinitialize
            initialValues={formData}
            validationSchema={scheduleFormValidationSchema}
            onSubmit={handleFormSubmit}
          >
            {showLoader ? (
              <LogoLoader />
            ) : (
              formikBag => {
                formikRef.current = formikBag;
                const { values, setFieldValue, errors, touched, validateForm } = formikBag;
                return (
                  <Form noValidate autoComplete="off">
                    {flag ? (
                      <EmployeesAndGroups />
                    ) : (
                      <>
                        <FormikInput
                          type="text"
                          name="title"
                          label={t('common:title')}
                          formControlProps={{ isRequired: true }}
                        />
                        {/* Employees */}
                        {userStore?.userInfo?.roleId !== roles.EMPLOYEE ? (
                          <FormControl
                            mb="6"
                            id="user_ids"
                            isInvalid={(!!errors.user_ids && !!touched.user_ids) || false}
                            isRequired
                          >
                            <FormLabel
                              color="gray.500"
                              fontSize="12px"
                              fontWeight="600"
                              mb="1"
                              textTransform="uppercase"
                            >
                              {t('scheduling:userOrGroup')}
                            </FormLabel>
                            <InputGroup size="lg">
                              <Input
                                fontSize="sm"
                                variant="filled"
                                type="text"
                                border="1px"
                                borderColor="inherit"
                                onChange={() => null}
                                value={`${values.user_ids.length || t('common:select')} ${t('common:users')}`}
                              />
                              <InputRightElement
                                width="auto"
                                mr="2"
                                children={
                                  <Button size="sm" variant="link" onClick={setFlag.on}>
                                    {values.user_ids.length ? 'Edit' : 'Add'}
                                  </Button>
                                }
                              />
                            </InputGroup>
                            <ErrorMessage
                              name="user_ids"
                              render={(error: any) => (
                                <FormErrorMessage fontSize="13px" mt="1">
                                  {t(error.msg, { value: error.value })}
                                </FormErrorMessage>
                              )}
                            />
                          </FormControl>
                        ) : null}
                        <FormikSelectAsyncPaginate
                          name="job_id"
                          label={t('scheduling:jobName')}
                          loadOptions={(search: any, _: any, { page }: any) =>
                            jobLoadOptions(search, page, values.user_ids)
                          }
                          selectProps={{
                            placeholder: t('common:selectJob'),
                            getOptionLabel: (option: any) => option.name,
                            getOptionValue: (option: any) => option.id,
                            isDisabled: values.user_ids.length ? false : true,
                          }}
                          onChangeCallback={(option: any) => handleShowAddress(option, setFieldValue)}
                        />
                        <FormikCheckbox
                          options={[{ label: t('common:useADifferentAddress'), value: 'yes' }]}
                          name="show_address"
                          stackProps={{ mr: 5 }}
                        />
                        {values.show_address?.includes('yes') ? (
                          <Box>
                            <FormikInput
                              type="text"
                              name="street"
                              label={t('common:street')}
                              formControlProps={{
                                isRequired: true,
                              }}
                            />
                            <SimpleGrid columns={{ md: 2 }} spacingX={{ md: 5 }}>
                              <FormikInput
                                type="text"
                                name="city"
                                label={t('common:city')}
                                formControlProps={{
                                  isRequired: true,
                                }}
                              />
                              <FormikInput
                                type="text"
                                name="state"
                                label={t('common:state')}
                                formControlProps={{
                                  isRequired: true,
                                }}
                              />
                              <FormikInput
                                type="text"
                                name="country"
                                label={t('common:country')}
                                formControlProps={{
                                  isRequired: true,
                                }}
                              />
                              <FormikInput
                                type="text"
                                name="zipCode"
                                label={t('common:zipCode')}
                                formControlProps={{
                                  isRequired: true,
                                }}
                              />
                            </SimpleGrid>
                            <Divider mb={2} variant="dashed" borderColor="gray.500" />
                          </Box>
                        ) : null}
                        <FormikTextArea name="description" label={t('common:description')} />
                        <>
                          <SimpleGrid columns={2} spacing="2">
                            <FormikDatepicker
                              label={t('common:startDate')}
                              name="start_date"
                              datepickerProps={{ popperPlacement: 'bottom-start' }}
                              handleChangeCallback={date => {
                                if (date && values.end_date <= date) {
                                  setFieldValue('end_date', undefined);
                                }
                              }}
                              formControlProps={{
                                isRequired: true,
                              }}
                            />
                            <FormikInput
                              type="time"
                              label={t('common:startTime')}
                              name="start_time"
                              inputProps={{ max: values.end_time }}
                              formControlProps={{ isRequired: true, isDisabled: values.all_day.includes('yes') }}
                            />
                          </SimpleGrid>
                          <SimpleGrid columns={2} spacing="2">
                            <FormikDatepicker
                              label={t('common:endDate')}
                              name="end_date"
                              datepickerProps={{ minDate: values.start_date || null, popperPlacement: 'bottom-start' }}
                              formControlProps={{ isRequired: true }}
                            />
                            <FormikInput
                              type="time"
                              label={t('common:endTime')}
                              name="end_time"
                              inputProps={{ min: values.start_time }}
                              formControlProps={{ isRequired: true, isDisabled: values.all_day.includes('yes') }}
                            />
                          </SimpleGrid>
                        </>

                        <FormikSelect
                          name="timezone"
                          label={t('common:timezone')}
                          options={timezoneOptions}
                          selectProps={{
                            placeholder: t('scheduling:selectTimezone'),
                            isClearable: false,
                            isLoading: timezoneStatus === 'loading',
                            value:
                              typeof values.timezone === 'object'
                                ? values.timezone
                                : (timezoneOptions || []).find((option: any) => option.value === values.timezone) || '',
                          }}
                        />
                        <Box mb={{ base: 4, md: 0 }}>
                          <FormikCheckbox
                            options={[{ label: t('common:allDay'), value: 'yes' }]}
                            name="all_day"
                            onChangeCallback={(e: React.ChangeEvent<HTMLInputElement>) =>
                              handleAllDayChangeCallback(e, setFieldValue)
                            }
                            stackProps={{ mr: 5 }}
                          />
                          <FormikCheckbox options={[{ label: t('common:repeat'), value: 'yes' }]} name="repeat" />
                        </Box>
                        {values.repeat.includes('yes') ? (
                          <>
                            <FormikSelect
                              name="repeat_type"
                              label={t('common:repeatType')}
                              options={repeatTypeOptions}
                            />
                            <FormikDatepicker
                              label={t('scheduling:repeatEndDate')}
                              name="repeat_end_date"
                              datepickerProps={{
                                minDate: values.end_date,
                                maxDate: new Date(
                                  new Date().setFullYear(new Date(values.start_date).getFullYear() + 1)
                                ),
                                popperPlacement: 'bottom-start',
                              }}
                            />
                            {values.repeat_type &&
                            values.repeat_type?.value !== 'daily' &&
                            values.repeat_type?.value !== 'monthly' ? (
                              <Box mb={4}>
                                <Text
                                  color="gray.500"
                                  fontSize="12px"
                                  fontWeight="600"
                                  mb="1"
                                  textTransform="uppercase"
                                >
                                  {t('scheduling:repeatDays')}
                                </Text>
                                <DayPicker
                                  value={values.weeks}
                                  handleChange={(day: any) => handleDayChange(values.weeks, day, setFieldValue)}
                                />
                              </Box>
                            ) : null}
                          </>
                        ) : null}
                        <Text color="gray.500" fontSize="12px" fontWeight="600" mb="1" textTransform="uppercase">
                          {t('common:color')}
                        </Text>
                        <ReactColor
                          selectedColor={values.color}
                          colors={colorsData?.payload ? colorsData?.payload.map((item: any) => item?.hex_code) : []}
                          handleChange={(color: ColorResult) => setFieldValue('color', color.hex)}
                        />
                        <DrawerFixedButton>
                          <SimpleGrid columns={2} spacing={3}>
                            {!values.id || (values.id && !values.publish) ? (
                              <Button
                                type="submit"
                                bg="secondary.50"
                                color="secondary.400"
                                _hover={{
                                  bg: 'secondary.100',
                                }}
                                _active={{
                                  bg: 'secondary.100',
                                }}
                                width="100%"
                                size="lg"
                                isLoading={!values.publish && status === 'loading'}
                              >
                                {values.id ? t('scheduling:updateAsDraft') : t('scheduling:saveAsDraft')}
                              </Button>
                            ) : null}
                            <Button
                              type={
                                0 !==
                                  new Date(values.start_date).getTime() -
                                    new Date(values.repeat_start_date).getTime() && values.update_series
                                  ? 'button'
                                  : 'submit'
                              }
                              colorScheme="secondary"
                              width="100%"
                              size="lg"
                              onClick={() => {
                                setFieldValue('publish', true);
                                if (
                                  0 !==
                                    new Date(values.start_date).getTime() -
                                      new Date(values.repeat_start_date).getTime() &&
                                  values.update_series
                                ) {
                                  validateForm();
                                  if (Object.keys(errors || {}).length === 0) {
                                    onConfirmEditEntireShiftModalOpen();
                                  }
                                }
                              }}
                              isLoading={values.publish && status === 'loading'}
                            >
                              {t('common:publish')}
                            </Button>
                          </SimpleGrid>
                        </DrawerFixedButton>
                      </>
                    )}
                    <ConfirmationModal
                      isOpen={isConfirmEditEntireShiftModalOpen}
                      onClose={onConfirmEditEntireShiftModalClose}
                      title={t('scheduling:doYouWAntToEditFromStartingShiftDate')}
                      confirmButtonProps={{
                        type: 'submit',
                        onClick: () => {
                          setAllShift(false);
                          if (formikRef.current) {
                            formikRef.current.handleSubmit();
                          }
                        },
                      }}
                      confirmButtonLabel={
                        <Box>{`${t('scheduling:selectedShiftDate')} ${
                          values.start_date
                            ? format(
                                new Date(values.start_date),
                                companyInfo?.companyDateFormat || MONTH_DAY_YEAR_FORMAT
                              )
                            : ''
                        }`}</Box>
                      }
                      cancelButtonLabel={
                        <Box>{`${t('scheduling:repeatShiftStartDate')} ${
                          values.repeat_start_date
                            ? format(
                                new Date(values.repeat_start_date),
                                companyInfo?.companyDateFormat || MONTH_DAY_YEAR_FORMAT
                              )
                            : ''
                        }`}</Box>
                      }
                      cancelButtonProps={{
                        type: 'submit',
                        variant: 'solid',
                        onClick: () => {
                          setAllShift(true);
                          if (formikRef.current) {
                            formikRef.current.handleSubmit();
                          }
                        },
                      }}
                    />
                  </Form>
                );
              }
            )}
          </Formik>
        </DrawerBody>
      </DrawerContent>
    </>
  );
};

export default ScheduleForm;
